import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// Extend jsPDF type to include autoTable
declare module "jspdf" {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}

interface OrderData {
  id: string;
  title: string;
  description?: string;
  status: number;
  contractType: string;
  isHourly: boolean;
  weeklyLimit?: number;
  amount: number;
  weeklyAmount?: number;
  emergencyCost?: number;
  trackerCost?: number;
  platformFee?: number;
  totalAmount: number;
  dueDate: string;
  location: string;
  client: string;
  clientRating?: number;
  clientInfo?: {
    completedJobs?: number;
    totalJobs?: number;
    location?: string;
    address?: string;
  };
  messages: number;
  attachments: number;
  files?: Array<{
    id: string;
    path: string;
  }>;
  milestones?: Array<{
    id: string;
    description: string;
    amount: number;
    status: { id: number };
    paymentStatus: string;
    dueDate?: string;
    trackerAmount?: number;
    emergencyCost?: number;
  }>;
}

export const generateOrderSummaryPDF = (order: OrderData): void => {
  const doc = new jsPDF();
  
  // Set up fonts and colors
  const primaryColor = "#1f2937"; // gray-800
  const secondaryColor = "#6b7280"; // gray-500
  const accentColor = "#3b82f6"; // blue-600
  
  // Helper function to get contract status text
  const getContractStatusText = (status: number): string => {
    const statusMap: { [key: number]: string } = {
      1: "Pending",
      2: "Approved",
      3: "Accepted",
      4: "Rejected",
      5: "Completed",
      6: "Cancelled",
      7: "Archived"
    };
    return statusMap[status] || "Unknown";
  };
  
  // Helper function to get milestone status text
  const getMilestoneStatusText = (statusId: number): string => {
    const statusMap: { [key: number]: string } = {
      1: "Pending",
      2: "Approved",
      3: "Accepted",
      4: "Rejected",
      5: "Completed"
    };
    return statusMap[statusId] || "Unknown";
  };
  
  // Helper function to get payment status text
  const getPaymentStatusText = (status: string): string => {
    const statusMap: { [key: string]: string } = {
      "no_payment": "No Payment",
      "pending": "Pending",
      "funded": "Funded",
      "requested": "Requested",
      "released": "Released",
      "rejected": "Rejected"
    };
    return statusMap[status] || status;
  };
  
  // Title
  doc.setFontSize(20);
  doc.setTextColor(primaryColor);
  doc.text("Order Summary", 20, 30);
  
  // Order ID and Date
  doc.setFontSize(12);
  doc.setTextColor(secondaryColor);
  doc.text(`Order ID: ${order.id}`, 20, 45);
  doc.text(`Generated: ${new Date().toLocaleDateString()}`, 20, 52);
  
  // Order Details Section
  doc.setFontSize(16);
  doc.setTextColor(primaryColor);
  doc.text("Order Details", 20, 70);
  
  // Order details table
  const orderDetails = [
    ["Title", order.title],
    ["Status", getContractStatusText(order.status)],
    ["Contract Type", order.isHourly ? "Hourly" : "Fixed Price"],
    ["Base Amount", order.isHourly ? `$${order.amount.toFixed(2)}/hour` : `$${order.amount.toFixed(2)}`],
    ["Due Date", new Date(order.dueDate).toLocaleDateString()],
    ["Location", order.location]
  ];
  
  if (order.isHourly && order.weeklyLimit) {
    orderDetails.push(["Weekly Hours", `${order.weeklyLimit} hours`]);
    orderDetails.push(["Weekly Amount", `$${order.weeklyAmount?.toFixed(2) || "0.00"}`]);
  }
  
  if (order.emergencyCost && order.emergencyCost > 0) {
    orderDetails.push(["Emergency Cost", `+$${order.emergencyCost.toFixed(2)}`]);
  }
  
  if (order.trackerCost && order.trackerCost > 0) {
    orderDetails.push(["Smart Work Tracker", "Enabled"]);
  }
  
  orderDetails.push(["Total Amount", `$${(order.totalAmount + (order.emergencyCost || 0)).toFixed(2)}`]);
  
  autoTable(doc, {
    startY: 75,
    head: [["Property", "Value"]],
    body: orderDetails,
    theme: "grid",
    headStyles: {
      fillColor: accentColor,
      textColor: 255,
      fontStyle: "bold"
    },
    bodyStyles: {
      textColor: primaryColor
    },
    columnStyles: {
      0: { cellWidth: 60 },
      1: { cellWidth: 100 }
    }
  });
  
  // Client Information Section
  const clientY = (doc as any).lastAutoTable.finalY + 20;
  doc.setFontSize(16);
  doc.setTextColor(primaryColor);
  doc.text("Client Information", 20, clientY);
  
  const clientDetails = [
    ["Client Name", order.client],
    ["Rating", order.clientRating ? `${order.clientRating}/5` : "Not rated"],
    ["Total Jobs", `${order.clientInfo?.completedJobs || order.clientInfo?.totalJobs || "Not specified"} jobs`],
    ["Location", order.clientInfo?.location || order.clientInfo?.address || "Not specified"],
    ["Messages", `${order.messages} messages`],
    ["Attachments", `${order.attachments} files`]
  ];
  
  autoTable(doc, {
    startY: clientY + 10,
    head: [["Property", "Value"]],
    body: clientDetails,
    theme: "grid",
    headStyles: {
      fillColor: accentColor,
      textColor: 255,
      fontStyle: "bold"
    },
    bodyStyles: {
      textColor: primaryColor
    },
    columnStyles: {
      0: { cellWidth: 60 },
      1: { cellWidth: 100 }
    }
  });
  
  // Contract Files Section
  if (order.files && order.files.length > 0) {
    const filesY = (doc as any).lastAutoTable.finalY + 20;
    doc.setFontSize(16);
    doc.setTextColor(primaryColor);
    doc.text("Contract Files", 20, filesY);
    
    const fileDetails = order.files.map((file, index) => [
      `File ${index + 1}`,
      file.path?.split("/").pop() || `File ${index + 1}`,
      "Contract Document"
    ]);
    
    autoTable(doc, {
      startY: filesY + 10,
      head: [["#", "File Name", "Type"]],
      body: fileDetails,
      theme: "grid",
      headStyles: {
        fillColor: accentColor,
        textColor: 255,
        fontStyle: "bold"
      },
      bodyStyles: {
        textColor: primaryColor
      }
    });
  }
  
  // Milestones Section
  if (order.milestones && order.milestones.length > 0) {
    const milestonesY = (doc as any).lastAutoTable.finalY + 20;
    doc.setFontSize(16);
    doc.setTextColor(primaryColor);
    doc.text("Contract Milestones", 20, milestonesY);
    
    const milestoneDetails = order.milestones.map((milestone, index) => [
      `Milestone ${index + 1}`,
      milestone.description,
      `$${milestone.amount}`,
      getMilestoneStatusText(milestone.status?.id),
      getPaymentStatusText(milestone.paymentStatus),
      milestone.dueDate ? new Date(milestone.dueDate).toLocaleDateString() : "Not set"
    ]);
    
    autoTable(doc, {
      startY: milestonesY + 10,
      head: [["#", "Description", "Amount", "Status", "Payment", "Due Date"]],
      body: milestoneDetails,
      theme: "grid",
      headStyles: {
        fillColor: accentColor,
        textColor: 255,
        fontStyle: "bold"
      },
      bodyStyles: {
        textColor: primaryColor
      },
      columnStyles: {
        0: { cellWidth: 20 },
        1: { cellWidth: 60 },
        2: { cellWidth: 25 },
        3: { cellWidth: 25 },
        4: { cellWidth: 25 },
        5: { cellWidth: 25 }
      }
    });
  }
  
  // Contract Summary Section
  const summaryY = (doc as any).lastAutoTable.finalY + 20;
  doc.setFontSize(16);
  doc.setTextColor(primaryColor);
  doc.text("Contract Summary", 20, summaryY);
  
  const summaryDetails = [
    ["Contract Type", order.isHourly ? "Hourly" : "Fixed Price"],
    ["Base Amount", order.isHourly ? `$${order.amount.toFixed(2)}/hour` : `$${order.amount.toFixed(2)}`]
  ];
  
  if (order.isHourly && order.weeklyLimit) {
    summaryDetails.push(["Weekly Hours", `${order.weeklyLimit} hours`]);
    summaryDetails.push(["Weekly Amount", `$${order.weeklyAmount?.toFixed(2) || "0.00"}`]);
  }
  
  if (order.emergencyCost && order.emergencyCost > 0) {
    summaryDetails.push(["Emergency Cost", `+$${order.emergencyCost.toFixed(2)}`]);
  }
  
  if (order.trackerCost && order.trackerCost > 0) {
    summaryDetails.push(["Smart Work Tracker", "Enabled"]);
  }
  
  summaryDetails.push(["Total Contract Value", `$${(order.totalAmount + (order.emergencyCost || 0)).toFixed(2)}`]);
  
  autoTable(doc, {
    startY: summaryY + 10,
    head: [["Property", "Value"]],
    body: summaryDetails,
    theme: "grid",
    headStyles: {
      fillColor: accentColor,
      textColor: 255,
      fontStyle: "bold"
    },
    bodyStyles: {
      textColor: primaryColor
    },
    columnStyles: {
      0: { cellWidth: 60 },
      1: { cellWidth: 100 }
    }
  });
  
  // Footer
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(secondaryColor);
    doc.text(
      `Page ${i} of ${pageCount} | Generated on ${new Date().toLocaleString()}`,
      20,
      doc.internal.pageSize.height - 10
    );
  }
  
  // Save the PDF
  const fileName = `Order_Summary_${order.id}_${new Date().toISOString().split("T")[0]}.pdf`;
  doc.save(fileName);
};

export default generateOrderSummaryPDF;